import { useState, useEffect, useCallback } from 'react';
import {
  isTelegramWebApp,
  isLikelyTelegramEnvironment,
  loadTelegramSdk,
  getRawInitData,
  getUnsafeUserData,
  expandTelegramApp,
  getTelegramTheme,
  authenticateWithTelegram
} from './TelegramAuth';
import { ApiPaths } from '../Config/ApiPath';

// REACT_APP_API_BASE_URL is the variable the rest of the app uses (see Common/AxiosHelper.js);
// REACT_APP_API_URL is kept first only for backward compatibility with older deployments.
const baseURL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://apis.uniquefinance.cloud/user';

/**
 * Custom Hook for Telegram Mini App Authentication
 *
 * Automatically detects Telegram users and handles the authentication flow.
 */
export const useTelegramAuth = () => {
  const [isTelegram, setIsTelegram] = useState(false);
  const [userData, setUserData] = useState(null);
  const [rawInitData, setRawInitData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [theme, setTheme] = useState('light');

  /**
   * Authenticate user with the backend
   */
  const authenticateUser = useCallback(async (initData) => {
    try {
      console.log('🔄 Starting authentication process...');
      setLoading(true);
      setError(null);

      // Backend API endpoint
      const apiEndpoint = `${baseURL}${ApiPaths.telegramAuth}`;
      console.log('🌐 Making request to:', apiEndpoint);
      console.log('📤 InitData length:', initData.length);
      console.log('📤 InitData preview:', initData.substring(0, 100) + '...');

      const response = await authenticateWithTelegram(initData, apiEndpoint);
      console.log('✅ Authentication response received:', response);
      
      if (response.token) {
        console.log('🎉 Authentication successful!');
        // JWT token received
        setToken(response.token);
        setIsAuthenticated(true);

        // Save token to localStorage
        localStorage.setItem('token', response.token);
        if (response.user?.id) {
          localStorage.setItem('userId', response.user.id);
        }
        if (response.user?.telegram_id) {
          localStorage.setItem('telegramId', response.user.telegram_id);
        }
        localStorage.setItem('telegramAuth', 'true');
        console.log('💾 Token saved to localStorage');
        
        // Update user data
        if (response.user) {
          setUserData(response.user);
        }
      } else {
        console.error('❌ Token not received from server');
        setError('Token not received from server');
      }
    } catch (err) {
      console.error('❌ Authentication error:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error stack:', err.stack);
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Function to check and authenticate
    const checkAndAuthenticate = () => {
      console.log('🔍 Checking Telegram environment...');
      
      // Check if running in Telegram
      const isTG = isTelegramWebApp();
      console.log('📱 Is Telegram WebApp:', isTG);
      
      setIsTelegram(isTG);

      if (isTG) {
        // Expand Telegram app to full height
        expandTelegramApp();

        // Unsafe user data for UI display
        const unsafeUser = getUnsafeUserData();
        console.log('👤 Unsafe User Data:', unsafeUser);
        setUserData(unsafeUser);

        // Raw initData for server verification
        const rawData = getRawInitData();
        console.log('📦 Raw InitData:', rawData ? 'Available' : 'Not Available');
        console.log('📦 Raw InitData Length:', rawData ? rawData.length : 0);
        setRawInitData(rawData);

        // Detect theme
        const currentTheme = getTelegramTheme();
        setTheme(currentTheme);

        // Check if already authenticated (token exists in localStorage)
        const existingToken = localStorage.getItem('token');
        const isTelegramAuth = localStorage.getItem('telegramAuth') === 'true';
        console.log('🔑 Existing Token:', existingToken ? 'Found' : 'Not Found');
        console.log('🔑 Telegram Auth Flag:', isTelegramAuth);
        
        if (existingToken && isTelegramAuth) {
          // Already authenticated, just set state
          console.log('✅ Already authenticated, using existing token');
          setToken(existingToken);
          setIsAuthenticated(true);
          setLoading(false);
        } else if (rawData && rawData.length > 0) {
          // Auto-authenticate with backend
          console.log('🚀 Starting authentication with backend...');
          console.log('🌐 API Endpoint:', `${baseURL}${ApiPaths.telegramAuth}`);
          authenticateUser(rawData);
        } else {
          console.error('❌ InitData not available');
          setLoading(false);
          setError('InitData not available');
        }

        // Theme change listener
        const tg = window.Telegram?.WebApp;
        if (tg && tg.onEvent) {
          tg.onEvent('themeChanged', () => {
            setTheme(getTelegramTheme());
          });
        }
      } else {
        // Not in Telegram — skip auth
        console.log('ℹ️ Not running in Telegram, skipping authentication');
        setLoading(false);
      }
    };

    // Wait for Telegram SDK only inside Telegram; normal browsers skip immediately
    if (typeof window !== 'undefined') {
      if (window.Telegram && window.Telegram.WebApp) {
        console.log('✅ Telegram SDK already loaded');
        checkAndAuthenticate();
      } else if (!isLikelyTelegramEnvironment()) {
        console.log('ℹ️ Not in Telegram environment — skip SDK wait');
        setLoading(false);
      } else {
        console.log('⏳ Loading Telegram SDK dynamically...');
        loadTelegramSdk().then((loaded) => {
          if (loaded) {
            checkAndAuthenticate();
          } else {
            console.error('❌ Telegram SDK failed to load');
            setLoading(false);
          }
        });
      }
    }
  }, [authenticateUser]);

  /**
   * Manual re-authentication
   */
  const reAuthenticate = () => {
    const rawData = getRawInitData();
    if (rawData) {
      authenticateUser(rawData);
    } else {
      setError('InitData not available');
    }
  };

  /**
   * Logout function
   */
  const logout = () => {
    setToken(null);
    setIsAuthenticated(false);
    setUserData(null);
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('telegramId');
    localStorage.removeItem('telegramAuth');
  };

  return {
    isTelegram,
    userData,
    rawInitData,
    isAuthenticated,
    loading,
    error,
    token,
    theme,
    reAuthenticate,
    logout
  };
};
